<script setup lang="ts">
// 위장 화면(웹). 숨기기는 쉽게(부모가 Esc·핫코너·자리비움으로 표시), 다시 열기는 비밀번호로만.
// 위장 화면은 진짜처럼 쓸 수 있다: 셀 입력/문서 편집/메일 열기/카드 옮기기.
import { onMounted, onUnmounted, reactive, ref, computed, nextTick, watch } from 'vue'
import type { CoverId } from '../stores/ui'
import { useVaultStore } from '../stores/vault'

const props = defineProps<{ cover: CoverId; pin: string }>()
const emit = defineEmits<{ unlock: [] }>()
const vault = useVaultStore()

/* ── 시트 ── */
const COLS = 'ABCDEFGH'.split('')
const seed: Record<string, string> = {
  A2: '항목', B2: '1월', C2: '2월', D2: '3월', E2: '합계',
  A3: '인건비', B3: '42,300', C3: '42,300', D3: '43,100', E3: '127,700',
  A4: '마케팅', B4: '8,200', C4: '11,500', D4: '9,800', E4: '29,500',
  A5: '외주비', B5: '5,000', C5: '5,000', D5: '7,400', E5: '17,400',
  A6: '교육훈련', B6: '1,200', C6: '900', D6: '1,500', E6: '3,600',
  A7: '합계', B7: '56,700', C7: '59,700', D7: '61,800', E7: '178,200',
}
const cells = reactive<Record<string, string>>({ ...seed })
const formulas: Record<string, string> = { D7: '=SUM(D3:D6)', E7: '=SUM(B7:D7)' }
const sel = ref('D7')
const editing = ref('')
const editVal = ref('')
const fxVal = computed(() => formulas[sel.value] || cells[sel.value] || '')

const ROWS = 34
function ref_(c: number, r: number) { return `${COLS[c]}${r + 2}` }
function selectCell(ref: string) { if (editing.value) commit(false); sel.value = ref }
// 진짜 입력창으로 편집한다(IME 정상). 포커스는 렌더 뒤에 — v-for 안 ref는 배열로 잡혀 못 쓰므로 querySelector로.
function startEdit(ref: string, init: string) {
  editing.value = ref; editVal.value = init
  nextTick(() => (document.querySelector('.xl-edit') as HTMLInputElement | null)?.focus())
}
// 입력값이 PIN·앱 비번과 맞으면 Enter·IME와 무관하게 바로 해제한다. 비싼 verify는 디바운스.
let verifyT: number | undefined
watch(editVal, (val) => {
  clearTimeout(verifyT)
  const typed = val.trim().replace(/^=/, '')
  if (!typed) return
  if (typed === props.pin) { commit(true); emit('unlock'); return } // PIN: 즉시(싸다)
  verifyT = window.setTimeout(async () => {
    if (editing.value && await vault.verifyPassword(typed)) { commit(true); emit('unlock') }
  }, 400)
})
async function sheetEnter() {
  // Enter로도 해제되게(앱 비번은 Enter가 디바운스 검증을 가로채던 문제). 비번 아니면 시트처럼 아래로.
  const typed = editVal.value.trim().replace(/^=/, '')
  if (typed === props.pin || (typed && await vault.verifyPassword(typed))) { commit(true); emit('unlock'); return }
  commit(false); move(1, 0)
}
function sheetTab() { commit(false); move(0, 1) }
function commit(cancel: boolean) {
  if (!editing.value) return
  if (!cancel) { cells[editing.value] = editVal.value; delete formulas[editing.value] }
  editing.value = ''
}
function move(dr: number, dc: number) {
  const c = COLS.indexOf(sel.value[0]); const r = parseInt(sel.value.slice(1)) - 2
  const nc = Math.max(0, Math.min(7, c + dc)); const nr = Math.max(0, Math.min(ROWS - 1, r + dr))
  selectCell(ref_(nc, nr))
}

/* ── 공통: 비밀번호 입력 버퍼(시트 외 위장) ── */
let buf = ''

/* ── 메일 ── */
const MAILS = [
  { from: '인사팀', subj: '[안내] 연말정산 간소화 자료 제출 일정', time: '14:02', u: true },
  { from: 'IT지원', subj: '정기 보안 점검 (10/25 토)', time: '11:48', u: true },
  { from: '재무팀', subj: '법인카드 사용 내역 확인 요청', time: '10:15', u: false },
  { from: '교육담당', subj: '하반기 필수교육 이수 현황', time: '어제', u: false },
  { from: '총무팀', subj: '사내 동호회 신청 마감', time: '10월 20일', u: false },
  { from: '보안팀', subj: '피싱 메일 주의 안내', time: '10월 19일', u: false },
]
const openMail = ref<number | null>(null)

/* ── 칸반 ── */
interface Card { id: string; text: string; p?: string; done?: boolean }
const kb = reactive<Record<string, Card[]>>({
  '할 일': [
    { id: 'PLT-231', text: 'API 응답 캐시 정책 정리', p: 'P2' },
    { id: 'PLT-240', text: '배포 체크리스트 문서화', p: 'P2' },
    { id: 'PLT-241', text: '에러 알림 중복 제거', p: 'P1' },
  ],
  '진행 중': [
    { id: 'PLT-224', text: '결제 모듈 타임아웃 재시도', p: 'P1' },
    { id: 'PLT-228', text: '관리자 권한 페이지 개편', p: 'P2' },
  ],
  '리뷰': [{ id: 'PLT-219', text: '회원 탈퇴 데이터 정리 배치', p: 'P2' }],
  '완료': [{ id: 'PLT-210', text: 'CI 캐시 적용', done: true }, { id: 'PLT-214', text: '스테이징 DB 분리', done: true }],
})
const KB_COLS = ['할 일', '진행 중', '리뷰', '완료']
let dragFrom = ''
let dragIdx = -1
function onDrop(col: string) {
  if (!dragFrom || dragIdx < 0) return
  const [card] = kb[dragFrom].splice(dragIdx, 1)
  kb[col].push(card); dragFrom = ''; dragIdx = -1
}

const hint = computed(() => {
  const map: Record<CoverId, string> = {
    sheet: '셀에 앱 비밀번호(또는 PIN)를 입력하면 열려요 · Enter 필요 없음',
    docs: '아무 데나 앱 비밀번호 입력 후 Enter',
    mail: '검색창이나 아무 데서 앱 비밀번호 + Enter',
    kanban: '앱 비밀번호 + Enter',
  }
  return `힌트 · ${map[props.cover]} (또는 PIN ${props.pin})`
})

/* ── 키보드: 숨기기는 부모, 열기는 비밀번호 ── */
async function onKey(e: KeyboardEvent) {
  if (props.cover === 'sheet') {
    if (editing.value) return // 편집 중엔 네이티브 입력창(v-model)이 처리한다(IME 정상)
    const mv: Record<string, [number, number]> = {
      ArrowDown: [1, 0], ArrowUp: [-1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1], Enter: [1, 0], Tab: [0, 1],
    }
    if (mv[e.key]) { e.preventDefault(); move(...mv[e.key]); return }
    if ((e.key === 'Backspace' || e.key === 'Delete') && sel.value) { cells[sel.value] = ''; delete formulas[sel.value]; return }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && sel.value) { e.preventDefault(); startEdit(sel.value, e.key) }
    return
  }
  // docs / mail / kanban: 공통 버퍼
  if (e.key === 'Escape') { if (props.cover === 'mail') openMail.value = null; return }
  if (e.isComposing) return
  if (e.key.length === 1) buf = (buf + e.key).slice(-64)
  if (e.key === 'Enter') {
    e.preventDefault()
    if (buf.endsWith(props.pin) || await vault.verifyPassword(buf)) { buf = ''; emit('unlock') }
  }
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <div class="cover" :class="cover">
    <!-- 스프레드시트 -->
    <template v-if="cover === 'sheet'">
      <div class="xl-bar"><div class="xl-ico"></div><b>2026 4분기 예산안_v3_최종</b></div>
      <div class="xl-menu"><span>파일</span><span>수정</span><span>보기</span><span>삽입</span><span>서식</span><span>데이터</span><span>도구</span></div>
      <div class="xl-fx"><span>{{ sel }}</span><b>{{ fxVal }}</b></div>
      <table class="xl">
        <tr><th></th><th v-for="c in COLS" :key="c">{{ c }}</th></tr>
        <tr v-for="r in ROWS" :key="r" :class="{ h: r === 2 }">
          <th>{{ r + 1 }}</th>
          <td
            v-for="(c, ci) in COLS" :key="c"
            :class="{ n: ci > 0, sel: sel === `${c}${r + 1}` && !editing }"
            @mousedown="selectCell(`${c}${r + 1}`)"
            @dblclick="startEdit(`${c}${r + 1}`, formulas[`${c}${r + 1}`] || cells[`${c}${r + 1}`] || '')"
          >
            <input v-if="editing === `${c}${r + 1}`" class="xl-edit" v-model="editVal" @keydown.enter.prevent="sheetEnter" @keydown.tab.prevent="sheetTab" @keydown.esc.prevent="commit(true)" />
            <template v-else>{{ cells[`${c}${r + 1}`] || '' }}</template>
          </td>
        </tr>
      </table>
    </template>

    <!-- 문서 -->
    <template v-else-if="cover === 'docs'">
      <div class="xl-bar"><div class="xl-ico" style="background:#5A6A80"></div><b>Q4 프로젝트 회고 (초안)</b></div>
      <div class="xl-menu"><span>파일</span><span>수정</span><span>보기</span><span>삽입</span><span>서식</span><span>도구</span></div>
      <div class="doc-page" contenteditable="true" spellcheck="false">
        <h1>Q4 프로젝트 회고 (초안)</h1>
        <p class="doc-meta">작성 중 · 마지막 수정 10월 22일</p>
        <h2>1. 목표 대비 결과</h2>
        <p>4분기 핵심 목표였던 온보딩 전환율 개선은 목표치 18% 대비 16.4%를 달성했다.</p>
        <h2>2. 잘된 점</h2>
        <ul><li>디자인–개발 간 주간 싱크를 고정해 재작업이 줄었다.</li><li>실험 결과를 대시보드로 공유해 의사결정 속도가 빨라졌다.</li></ul>
        <h2>3. 다음 분기 제안</h2>
        <p>지표 정의서를 먼저 합의하고, QA를 스프린트 중반으로 당긴다.</p>
      </div>
    </template>

    <!-- 메일함 -->
    <template v-else-if="cover === 'mail'">
      <div class="ml">
        <aside>
          <b class="ml-compose">메일 쓰기</b>
          <span class="on">받은편지함 <em>24</em></span><span>별표</span><span>보낸편지함</span><span>임시보관함</span><span>스팸</span>
        </aside>
        <div class="ml-list" v-if="openMail === null">
          <div class="ml-search"><input placeholder="메일 검색" autocomplete="off" /></div>
          <div v-for="(m, i) in MAILS" :key="i" class="ml-row" :class="{ u: m.u }" @click="openMail = i">
            <b>{{ m.from }}</b><span><strong>{{ m.subj }}</strong></span><time>{{ m.time }}</time>
          </div>
        </div>
        <div class="ml-list" v-else style="padding:20px 28px">
          <button style="border:0;background:none;color:#3A6EA5;cursor:pointer;padding:0;margin-bottom:16px" @click="openMail = null">← 목록으로</button>
          <h2 style="font-weight:400">{{ MAILS[openMail].subj }}</h2>
          <div style="color:#5a5a5a;font-size:13px;margin:6px 0 18px">{{ MAILS[openMail].from }} · {{ MAILS[openMail].time }}</div>
          <p>안녕하세요, {{ MAILS[openMail].from }}입니다. 관련하여 안내드립니다. 자세한 일정은 사내 게시판 공지를 확인해 주세요.</p>
        </div>
      </div>
    </template>

    <!-- 칸반 -->
    <template v-else>
      <div class="xl-bar"><div class="xl-ico" style="background:#6B6B78"></div><b>스프린트 42 · 플랫폼팀</b></div>
      <div class="kb">
        <div
          v-for="col in KB_COLS" :key="col" class="kb-col"
          @dragover.prevent @drop.prevent="onDrop(col)"
        >
          <h3>{{ col }} <em>{{ kb[col].length }}</em></h3>
          <div
            v-for="(card, i) in kb[col]" :key="card.id" class="kb-card" :class="{ done: card.done }"
            draggable="true" @dragstart="dragFrom = col; dragIdx = i"
          >
            <small>{{ card.id }}</small>{{ card.text }}<i v-if="card.p">{{ card.p }}</i>
          </div>
        </div>
      </div>
    </template>

    <div class="cv-hint">{{ hint }}</div>
  </div>
</template>
